import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, viewChild } from '@angular/core';
import {
  NgbDate,
  NgbDateParserFormatter,
  NgbDatepicker,
  NgbDatepickerMonth,
  NgbDateStruct,
} from '@ng-bootstrap/ng-bootstrap';
import { MatIcon } from '@angular/material/icon';
import { InputSize } from '../enum/input-size.enum';
import {
  NgxFloatUiContentComponent,
  NgxFloatUiDirective,
  NgxFloatUiPlacements,
  NgxFloatUiTriggers,
} from 'ngx-float-ui';
import { ButtonComponent } from '../buttons/button/button.component';
import { ButtonType } from '../enum/button-type.enum';
import { DateUtils } from '../../utils/date-utils';

@Component({
  selector: 'lib-date-picker',
  imports: [
    NgbDatepicker,
    NgbDatepickerMonth,
    MatIcon,
    NgxFloatUiDirective,
    NgxFloatUiContentComponent,
    ButtonComponent,
  ],
  templateUrl: './date-picker.component.html',
  styleUrl: './date-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatePickerComponent {
  private readonly ngbDateParserFormatter = inject(NgbDateParserFormatter);

  date = input<Date | null>();
  endDate = input<Date | null>();
  useRangeSelection = input(false);
  readonly minDate = input<string>();
  readonly maxDate = input<string>();
  readonly useFutureYearOptions = input<boolean>(false);
  protected readonly minimumAllowedDate = computed((): NgbDateStruct => {
    const minDate = this.minDate();
    const defaultMinDate = { year: 1, month: 1, day: 1 };
    return minDate ? (this.ngbDateParserFormatter.parse(minDate) ?? defaultMinDate) : defaultMinDate;
  });
  protected readonly maximumAllowedDate = computed((): NgbDateStruct => {
    const maxDate = this.maxDate();
    const defaultMaxDate = { year: 9999, month: 12, day: 31 };
    return maxDate ? (this.ngbDateParserFormatter.parse(maxDate) ?? defaultMaxDate) : defaultMaxDate;
  });
  result = output<DatePickerResult>();
  readonly hide = output<void>();

  datePicker = viewChild<NgbDatepicker>('datePicker');
  monthOptions = [
    { label: $localize`Jan`, value: 1 },
    { label: $localize`Feb`, value: 2 },
    { label: $localize`Mar`, value: 3 },
    { label: $localize`Apr`, value: 4 },
    { label: $localize`May`, value: 5 },
    { label: $localize`Jun`, value: 6 },
    { label: $localize`Jul`, value: 7 },
    { label: $localize`Aug`, value: 8 },
    { label: $localize`Sep`, value: 9 },
    { label: $localize`Oct`, value: 10 },
    { label: $localize`Nov`, value: 11 },
    { label: $localize`Dec`, value: 12 },
  ];

  currentYear = new Date().getFullYear();
  protected readonly yearOptions = computed((): { label: string; value: number }[] =>
    Array.from({ length: 100 }, (_, i) =>
      this.useFutureYearOptions()
        ? {
            label: (this.currentYear + i).toString(),
            value: this.currentYear + i,
          }
        : {
            label: (this.currentYear - i).toString(),
            value: this.currentYear - i,
          },
    ),
  );

  selectedDate: NgbDate | null = null;
  selectedEndRangeDate: NgbDate | null = null;

  protected readonly InputSize = InputSize;
  protected readonly NgxFloatUiTriggers = NgxFloatUiTriggers;
  protected readonly NgxFloatUiPlacements = NgxFloatUiPlacements;
  protected readonly ButtonType = ButtonType;

  inputDateEffect = effect(() => {
    const inputDate = this.date();
    const inputEndDate = this.endDate();
    if (inputDate) {
      this.selectedDate = DateUtils.dateToNgbDate(inputDate);
      if (inputEndDate) {
        this.selectedEndRangeDate = DateUtils.dateToNgbDate(inputEndDate);
      }
      if (this.selectedDate) {
        this.datePicker()?.navigateTo(this.selectedDate);
      }
    } else {
      this.selectedDate = null;
      this.selectedEndRangeDate = null;
    }
  });

  resetClicked() {
    this.selectedDate = null;
    this.selectedEndRangeDate = null;
    this.datePicker()?.navigateTo();
    this.result.emit(new DatePickerResult(null, null));
    this.hide.emit();
  }

  navigateMonth(number: number) {
    const datePicker = this.datePicker();
    if (datePicker) {
      const { state, calendar } = datePicker;
      datePicker.navigateTo(calendar.getNext(state.firstDate, 'm', number));
    }
  }

  monthSelected(monthNumber: number) {
    const datePicker = this.datePicker();
    if (datePicker) {
      const state = datePicker.state!;
      datePicker.navigateTo({
        year: state.firstDate.year,
        month: monthNumber,
      });
    }
  }

  yearSelected(year: number) {
    const datePicker = this.datePicker();
    if (datePicker) {
      const state = datePicker.state;
      datePicker.navigateTo({ year: year, month: state.firstDate.month });
    }
  }

  dateSelected(date: NgbDate) {
    if ((!this.selectedDate && !this.selectedEndRangeDate) || !this.useRangeSelection()) {
      this.selectedDate = date;
    } else if (this.selectedDate && !this.selectedEndRangeDate && date.after(this.selectedDate)) {
      this.selectedEndRangeDate = date;
    } else {
      this.selectedEndRangeDate = null;
      this.selectedDate = date;
    }
    this.result.emit(
      new DatePickerResult(
        DateUtils.ngbDateToDate(this.selectedDate),
        DateUtils.ngbDateToDate(this.selectedEndRangeDate),
      ),
    );
    this.hide.emit();
  }

  isInsideSelectedRange(date: NgbDate): boolean {
    return (
      this.selectedEndRangeDate !== null && date.after(this.selectedDate) && date.before(this.selectedEndRangeDate)
    );
  }

  isInsideMinMaxLimits(date: NgbDate): boolean {
    const minimumAllowedDate = this.minimumAllowedDate();
    const maximumAllowedDate = this.maximumAllowedDate();
    return (
      (!!minimumAllowedDate && date.before(minimumAllowedDate)) ||
      (!!maximumAllowedDate && date.after(maximumAllowedDate))
    );
  }
}

export class DatePickerResult {
  date: Date | null;
  endDate: Date | null;

  constructor(date: Date | null, endDate: Date | null) {
    this.date = date;
    this.endDate = endDate;
  }
}
